import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LATEST_WORK } from "../../data/content";
import { Chapter, Reveal } from "../shared";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function LatestWork() {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const [maxX, setMaxX] = useState(0);
  const [x, setX] = useState(0);
  const drag = useRef({ active: false, startX: 0, startVal: 0, moved: false });

  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      const wrap = wrapRef.current;
      if (el && wrap) {
        setMaxX(Math.max(0, el.scrollWidth - wrap.clientWidth));
        setX((v) => Math.max(-Math.max(0, el.scrollWidth - wrap.clientWidth), Math.min(0, v)));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const clamp = (v) => Math.max(-maxX, Math.min(0, v));

  const onPointerDown = (e) => {
    drag.current = { active: true, startX: e.clientX, startVal: x, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 5) drag.current.moved = true;
    setX(clamp(drag.current.startVal + dx));
  };
  const endDrag = () => { drag.current.active = false; };
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };
  const nudge = (dir) => setX((v) => clamp(v - dir * 440));

  return (
    <section className="bg-sand py-24 md:py-32" data-testid="latest-work">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Chapter num="07" title="Our Latest Work" />
        <div className="mt-6 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-emerald">
              Fresh from <span className="italic text-gold-text">our celebrations</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="flex items-center gap-4">
            <p className="hidden md:block max-w-sm text-sm leading-relaxed text-stone2">Newest weddings, decor, events and concepts — drag or use the arrows; your page always scrolls freely.</p>
            <button onClick={() => nudge(-1)} data-testid="latest-work-prev" aria-label="Previous" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 text-emerald transition-all hover:bg-gold hover:text-ink">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => nudge(1)} data-testid="latest-work-next" aria-label="Next" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 text-emerald transition-all hover:bg-gold hover:text-ink">
              <ChevronRight className="h-5 w-5" />
            </button>
          </Reveal>
        </div>
      </div>

      <div ref={wrapRef} className="mt-14 overflow-hidden px-6 md:px-12">
        <div
          ref={trackRef}
          data-testid="latest-work-track"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onClickCapture={onClickCapture}
          className="flex w-max cursor-grab select-none gap-6 active:cursor-grabbing"
          style={{ transform: `translateX(${x}px)`, transition: drag.current.active ? "none" : "transform 0.65s cubic-bezier(0.22, 1, 0.36, 1)", touchAction: "pan-y" }}
        >
          {LATEST_WORK.map((w, i) => (
            <Reveal key={i} delay={i * 0.05} className="shrink-0">
              <Link to={w.to} data-testid={`latest-work-card-${i}`} className="group relative block w-[280px] md:w-[400px]">
                <div className="overflow-hidden">
                  <img src={w.img} alt={w.title} loading="lazy" draggable={false} className="h-[380px] md:h-[520px] w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
                </div>
                <span className="absolute left-4 top-4 bg-emerald/90 px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] text-gold-light">{w.tag}</span>
                <div className="mt-4 flex items-baseline justify-between border-b border-gold/25 pb-4">
                  <h3 className="font-serif text-2xl text-emerald">{w.title}</h3>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-gold-text">{w.place}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
